// Wire-shape requests for /comments + /admin/comments + /admin/comments/
// blocklist.
import { z } from 'zod';
import { CommentBody } from '../primitives/editor-block.js';
import { CommentAttachment, CommentModerationMode, CommentReactionKind, CommentReportReason, CommentSortOrder, CommentStatus, ContentBlocklistAction, ContentBlocklistKind, } from './comment.js';
// ---------------------------------------------------------------------------
// CreateCommentRequest — POST /articles/:slug/comments.
//
// Authentication contract:
//   - Authenticated requests: server uses `session.user.id` as
//     `author_id`; the optional `guest` payload is REJECTED.
//   - Anonymous requests (no session): the `guest` payload IS REQUIRED
//     and the `comments.guest` flag must be on; otherwise the server
//     returns 401.
// ---------------------------------------------------------------------------
export const CreateCommentRequest = z.object({
    /** Top-level when `null` / omitted; reply when set to a sibling
     *  comment id on the same article. */
    parentId: z.string().uuid().nullable().optional(),
    /** Structured `Block[]` from the editor's comment-restricted preset
     *  (paragraph + quote-ref). Service walks inline content to derive
     *  `body_text` (plaintext) for the search index + blocklist matcher,
     *  and to extract mentioned user UUIDs from `<span class="vq-mention"
     *  data-entity-id="...">` atoms. */
    body: CommentBody,
    /** Attachments (non-image files; images live as inline body blocks).
     *  Service rejects when the
     *  `comments.attachments` flag is off OR the array exceeds the
     *  per-request cap (5). */
    attachments: z.array(CommentAttachment).max(5).optional(),
    /** Guest payload — only present (and required) for anonymous
     *  callers; logged-in callers MUST omit it. */
    guest: z
        .object({
        name: z.string().min(1).max(64),
        email: z.string().email().max(254),
    })
        .optional(),
});
// ---------------------------------------------------------------------------
// UpdateCommentRequest — PATCH /comments/:id.
//
// Body and attachments are the only mutable user-visible fields. Status
// transitions go through dedicated admin routes; ordinary edit calls
// can't poke `status` to `visible` to bypass moderation.
// ---------------------------------------------------------------------------
export const UpdateCommentRequest = z
    .object({
    body: CommentBody,
    attachments: z.array(CommentAttachment).max(5),
})
    .partial();
// ---------------------------------------------------------------------------
// ListCommentsQuery — GET /articles/:slug/comments.
//
// `parentId === null` → top-level only. `parentId === '<uuid>'` → just
// that thread. Omitted → top-level. Use the dedicated
// `GET /comments/:id/replies` route for paginated thread expansion (a
// shallow-tree page rendering doesn't need to fetch every nested reply
// up-front; deep threads are deferred-loaded).
// ---------------------------------------------------------------------------
export const ListCommentsQuery = z.object({
    parentId: z.string().uuid().nullable().optional(),
    threadRootId: z.string().uuid().optional(),
    /** Sort order — see `CommentSortOrder`. Defaults to `newest`. */
    sort: CommentSortOrder.optional(),
    /** Include comments the viewer technically can't see (admin-only —
     *  the route layer bounces non-managers if this is `true`). */
    includeHidden: z.boolean().optional(),
    limit: z.number().int().min(1).max(200).optional(),
    offset: z.number().int().min(0).optional(),
});
// ---------------------------------------------------------------------------
// ReactToCommentRequest — POST /comments/:id/reactions.
// ---------------------------------------------------------------------------
export const ReactToCommentRequest = z.object({
    kind: CommentReactionKind,
});
// ---------------------------------------------------------------------------
// ReportCommentRequest — POST /comments/:id/reports.
// ---------------------------------------------------------------------------
export const ReportCommentRequest = z.object({
    reason: CommentReportReason,
    details: z.string().max(500).optional(),
    /** Guest reporter contact email (used for follow-up communication
     *  + email blocklist match). Required when the caller has no
     *  session. Authenticated callers MUST omit it. */
    guestEmail: z.string().email().max(254).optional(),
});
// ---------------------------------------------------------------------------
// SubscribeRequest / UnsubscribeRequest — POST/DELETE /articles/:slug/
// comments/subscribe.
// Both bodies are empty — the user is identified by the session.
// ---------------------------------------------------------------------------
export const SubscribeRequest = z.object({}).strict();
// ---------------------------------------------------------------------------
// UpdateModerationSettingsRequest — PATCH /admin/articles/:slug/
// comments/settings.
//
// Each field is optional; the route layer applies a partial patch.
// `lockReason` is meaningful only when `locked === true` (the service
// clears it when the lock is released).
// ---------------------------------------------------------------------------
export const UpdateModerationSettingsRequest = z
    .object({
    mode: CommentModerationMode,
    locked: z.boolean(),
    lockReason: z.string().max(500).nullable(),
    notifyAuthor: z.boolean(),
})
    .partial();
// ---------------------------------------------------------------------------
// AdminBulkActionRequest — POST /admin/comments/bulk.
//
// `ids` is an explicit list (no "all visible matching X" — that would
// invite trigger-finger errors). Bulk capped at 200 per request.
// ---------------------------------------------------------------------------
export const AdminBulkActionRequest = z.object({
    ids: z.array(z.string().uuid()).min(1).max(200),
    action: z.enum(['approve', 'hide', 'spam', 'delete', 'unhide']),
});
// ---------------------------------------------------------------------------
// AdminListCommentsQuery — GET /admin/comments.
// ---------------------------------------------------------------------------
export const AdminListCommentsQuery = z.object({
    status: CommentStatus.optional(),
    articleId: z.string().uuid().optional(),
    authorId: z.string().uuid().optional(),
    limit: z.number().int().min(1).max(200).optional(),
    offset: z.number().int().min(0).optional(),
});
// ---------------------------------------------------------------------------
// AdminExportQuery — GET /admin/comments/export.
// ---------------------------------------------------------------------------
export const AdminExportQuery = z.object({
    format: z.enum(['csv', 'json']).optional(),
    articleId: z.string().uuid().optional(),
    status: CommentStatus.optional(),
    /** Hard-cap on rows — defence against an admin export accidentally
     *  pulling the whole comment table on a large site. Service applies a
     *  ceiling (10000) regardless of the supplied value. */
    limit: z.number().int().min(1).max(10_000).optional(),
});
// ---------------------------------------------------------------------------
// CreateBlocklistEntryRequest — POST /admin/comments/blocklist.
// ---------------------------------------------------------------------------
export const CreateBlocklistEntryRequest = z.object({
    kind: ContentBlocklistKind,
    value: z.string().min(1).max(256),
    action: ContentBlocklistAction.optional(),
});
//# sourceMappingURL=requests.js.map